import { useId, useRef, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useDebounced } from '@/api/useDebounced';
import { useLanguage } from '@/i18n/LanguageContext';
import { SkuMeta } from '@/components/SkuMeta';
import { ActionError, newOpId, plainQty, toScaled } from '@/pages/Exceptions/shared';
import type { InboundLine, ReceivingLocation, StockSkuHit } from '@/api/types';
import {
  DISCREPANCIES, PARTIES, describeReceivingError, discrepancyLabel, partyLabel, type DiscrepancyCode, type PartyCode,
} from './receivingModel';

const QTY_RE = /^\d+(\.\d{1,4})?$/;

/**
 * Manual receiving of one line, inline under it: quantity, receiving cell, an optional problem with
 * who is responsible, and - only when the product is unknown or differs from the document - a
 * product lookup by code, name or barcode (owner-scoped: only this seller's products). It posts to the
 * office receive route, which calls the same service the handheld does.
 *
 * `opId` is generated once per submission and kept for a retry of that same submission; it changes
 * only when the operator edits the form or after the receipt succeeded.
 */
export function ReceiveLinePanel({
  inboundId, ownerId, line, locations, warehouseName, onClose, onDone,
}: {
  readonly inboundId: string;
  readonly ownerId: string;
  readonly line: InboundLine;
  readonly locations: readonly ReceivingLocation[];
  readonly warehouseName: string;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): JSX.Element {
  const { t } = useLanguage();
  const uid = useId();
  const remaining = Math.max(0, Number(line.qty_expected) - Number(line.qty_received));
  const [qty, setQty] = useState(remaining > 0 ? plainQty(String(remaining)) : '1');
  // Receiving goes into the warehouse's receiving zone; quarantine is the explicit option for damaged or doubtful goods.
  const zones = locations.filter((l) => l.kind === 'RECEIVING');
  const quarantine = locations.filter((l) => l.kind === 'QUARANTINE');
  const [locationId, setLocationId] = useState(zones[0]?.id ?? '');
  const [discrepancy, setDiscrepancy] = useState<DiscrepancyCode>('NONE');
  const [party, setParty] = useState<PartyCode>('UNDECIDED');
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<StockSkuHit | null>(null);
  const [q, setQ] = useState('');
  const debounced = useDebounced(q.trim(), 250);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const opId = useRef(newOpId());
  const touch = (): void => {
    opId.current = newOpId();
  };

  const needsLookup = line.sku_id === null || discrepancy === 'WRONG_ITEM' || discrepancy === 'NO_BARCODE';
  const hits = useApi(
    () => (needsLookup && debounced.length >= 2 ? endpoints.stockSkuSearch({ q: debounced, ownerId }) : Promise.resolve(null)),
    [needsLookup, debounced, ownerId],
  );

  function validate(): string | null {
    if (!QTY_RE.test(qty.trim()) || toScaled(qty) <= BigInt(0)) return t('rcv.err.qty');
    if (!locationId) return t('rcv.err.location');
    if (line.sku_id === null && discrepancy !== 'NO_BARCODE' && !picked) return t('rcv.err.sku');
    if (discrepancy === 'WRONG_ITEM' && !picked) return t('rcv.err.sku');
    return null;
  }

  async function submit(): Promise<void> {
    if (busy) return;
    const problem = validate();
    setError(problem);
    if (problem) return;
    setBusy(true);
    try {
      const res = await endpoints.inboundReceive(inboundId, {
        opId: opId.current,
        inboundLineId: line.id,
        qty: qty.trim(),
        locationId,
        ...(needsLookup && picked ? { skuId: picked.sku_id } : {}),
        discrepancy,
        ...(discrepancy !== 'NONE' ? { responsibleParty: party } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      touch();
      onDone(t('rcv.done.line', { qty: plainQty(qty), received: plainQty(res.qtyReceivedTotal), expected: plainQty(line.qty_expected) }));
    } catch (err) {
      setError(describeReceivingError(t, err));
    } finally {
      setBusy(false);
    }
  }

  const ids = { qty: `${uid}-qty`, loc: `${uid}-loc`, disc: `${uid}-disc`, party: `${uid}-party`, note: `${uid}-note`, find: `${uid}-find` };

  return (
    <div className="card stock-panel receive-panel" role="group" aria-label={t('rcv.receiveLine')}>
      <h3 className="card-title">{t('rcv.receiveLine')}</h3>
      {error && <ActionError message={error} />}
      <div className="order-create">
        <div className="field-row">
          <label htmlFor={ids.qty}>{t('rcv.qty')}</label>
          <input id={ids.qty} className="qty-input" inputMode="decimal" value={qty} onChange={(e) => { setQty(e.target.value); touch(); }} />
        </div>
        <div className="field-row">
          <label htmlFor={ids.loc}>{t('rcv.location')}</label>
          <select id={ids.loc} value={locationId} onChange={(e) => { setLocationId(e.target.value); touch(); }}>
            {zones.map((l) => (
              <option key={l.id} value={l.id}>
                {t('rcv.zone.RECEIVING', { warehouse: warehouseName, code: l.code })}
              </option>
            ))}
            {quarantine.map((l) => (
              <option key={l.id} value={l.id}>
                {t('rcv.zone.QUARANTINE', { code: l.code })}
              </option>
            ))}
          </select>
          <p className="meta" data-testid="receive-flow-note">{t('rcv.flowNote')}</p>
          {quarantine.length === 0 && (discrepancy === 'DAMAGED' || discrepancy === 'EXPIRED') && (
            <p className="notice" role="status" data-testid="no-quarantine">{t('rcv.noQuarantine')}</p>
          )}
        </div>
        <div className="field-row">
          <label htmlFor={ids.disc}>{t('rcv.discrepancy')}</label>
          <select
            id={ids.disc}
            value={discrepancy}
            onChange={(e) => {
              setDiscrepancy(e.target.value as DiscrepancyCode);
              touch();
            }}
          >
            {DISCREPANCIES.map((d) => (
              <option key={d} value={d}>
                {discrepancyLabel(t, d)}
              </option>
            ))}
          </select>
        </div>
        {discrepancy !== 'NONE' && (
          <>
            <div className="field-row">
              <label htmlFor={ids.party}>{t('rcv.party')}</label>
              <select id={ids.party} value={party} onChange={(e) => { setParty(e.target.value as PartyCode); touch(); }}>
                {PARTIES.map((p) => (
                  <option key={p} value={p}>
                    {partyLabel(t, p)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-row">
              <label htmlFor={ids.note}>{t('rcv.note')}</label>
              <textarea id={ids.note} rows={2} maxLength={500} value={note} onChange={(e) => { setNote(e.target.value); touch(); }} />
            </div>
          </>
        )}

        {needsLookup && (
          <div className="sub-form">
            {picked ? (
              <p style={{ fontWeight: 600 }}>
                {t('rcv.pickedSku', { name: `${picked.code} — ${picked.name}` })}{' '}
                <button type="button" className="link-button" onClick={() => { setPicked(null); touch(); }}>
                  {t('rcv.change')}
                </button>
              </p>
            ) : (
              <>
                <label htmlFor={ids.find}>{t('rcv.find')}</label>
                <input id={ids.find} type="search" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
                <p className="meta">{t('rcv.findHint')}</p>
                {hits.status === 'success' && hits.data && (
                  hits.data.skus.length === 0 ? (
                    <p className="sub">{t('rcv.noHits')}</p>
                  ) : (
                    <ul className="sku-results" aria-label={t('rcv.find')}>
                      {hits.data.skus.map((h) => (
                        <li key={h.sku_id}>
                          <div>
                            <div>
                              {h.code} — {h.name}
                            </div>
                            <SkuMeta code={h.code} article={h.article} size={h.size} color={h.color} barcodes={h.barcodes} />
                            <div className="meta">{t('found.seller', { name: h.owner_name })}</div>
                            {h.is_serial_tracked && <div className="meta">{t('rcv.err.serial')}</div>}
                          </div>
                          <button
                            type="button"
                            disabled={h.is_serial_tracked}
                            onClick={() => { setPicked(h); touch(); }}
                            aria-label={`${t('rcv.pick')}: ${h.code}`}
                          >
                            {t('rcv.pick')}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )
                )}
              </>
            )}
          </div>
        )}

        <div className="form-actions action-row">
          <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
            {t('rcv.submit')}
          </button>
          <button type="button" onClick={onClose} disabled={busy}>
            {t('rcv.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
